import { createContext, ReactNode, useEffect } from 'react';
// utils
import { isValidToken } from '../utils/jwt';
// @types
import RestService from '../services/RestService';
import { AxiosResponse, isAxiosError } from 'axios';
import TokenService from '../services/TokenService';
import ErrorService from '../services/ErrorService';
import { dispatch, useSelector } from '../redux/store';
import {
  handleInitializeAction,
  handleLoginAction,
  handleLogoutAction,
} from '../redux/slices/auth';
import { AuthUser, JWTContextType, LoginResult } from '../redux/auth/auth.type';
import { accessRefusedError } from '../utils/accessRefused';

// ----------------------------------------------------------------------

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

export interface ILoginResponse {
  expiresAt?: number;
  refresh?: string;
  token?: string;
  // No session yet: the account needs a 2FA code ...
  twoFactorEnabled?: boolean;
  // ... or must enrol in 2FA first (done in the platform portal).
  twoFactorSetupRequired?: boolean;
  // The session ends after this long without activity (user-management).
  idleTimeoutSeconds?: number;
  data: AuthUser;
}

// Must match exception.PasswordExpiredMessage in user-management.
const PASSWORD_EXPIRED_MESSAGE = 'Password expired. Please reset your password';

function AuthProvider({ children }: AuthProviderProps) {
  const { isInitialized, isAuthenticated, user } = useSelector((state) => state.auth);
  useEffect(() => {
    const tokenData = TokenService.getLocalTokenData();
    const initialize = async () => {
      try {
        if (tokenData && tokenData.token && tokenData.data && isValidToken(tokenData?.expiresAt)) {
          TokenService.setLocalTokenData(tokenData);

          const parsedUser = TokenService.getUserDataFromToken(tokenData.token);
          const user: AuthUser = {
            ...parsedUser,
            ...tokenData.data,
          };

          dispatch(handleInitializeAction(true, user));
        } else {
          dispatch(handleInitializeAction(false, null));
        }
      } catch (err) {
        dispatch(handleInitializeAction(false, null));
      }
    };

    initialize();
  }, []);

  const login = async (
    email: string,
    password: string,
    otpCode?: string,
    useRecoveryCode?: boolean
  ): Promise<LoginResult> => {
    try {
      const response: AxiosResponse<ILoginResponse> = await RestService.login(
        email,
        password,
        otpCode,
        useRecoveryCode
      );
      const { token, twoFactorEnabled, twoFactorSetupRequired } = response.data;
      if (twoFactorSetupRequired) return { twoFactorSetupRequired: true };
      if (twoFactorEnabled && !token) return { twoFactorRequired: true };
      TokenService.setLocalTokenData(response.data);

      if (token) {
        const parsedUser = TokenService.getUserDataFromToken(token);
        const user: AuthUser = {
          ...parsedUser,
          ...response.data.data,
        };
        handleLoginAction(user)(dispatch);
      }
    } catch (e) {
      if (
        isAxiosError(e) &&
        e.response?.status === 401 &&
        (e.response?.data as { message?: string } | undefined)?.message === PASSWORD_EXPIRED_MESSAGE
      ) {
        return { passwordExpired: true };
      }
      const refused = accessRefusedError(e);
      if (refused) {
        // Shown in the sign-in form's own error banner, not as a toast.
        return { accessRefused: refused };
      }
      await ErrorService.handleErrorMessage(e);
    }
    return {};
  };

  const logout = async () => {
    const refreshToken = TokenService.getLocalRefreshToken();
    try {
      await RestService.logout(refreshToken || '');
    } catch (e) {
    } finally {
      handleLogoutAction()(dispatch);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isInitialized,
        user,
        method: 'jwt',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
